// Libraries
import _ from 'lodash';
import React from 'react';

// Supermove
import {
  DataLoader,
  Icon,
  RichTextInput,
  SanitizedHTML,
  ScrollView,
  Space,
  Styled,
} from '@supermove/components';
import {gql, useQuery} from '@supermove/graphql';
import {
  useResponsive,
  useState,
  useEffect,
  Form,
  MutationError,
  useRef,
  useToast,
} from '@supermove/hooks';
import {EmailModel, ProjectModel, ThreadModel, UserModel} from '@supermove/models';
import {colors} from '@supermove/styles';
import {ViewStyleProp} from '@supermove/styles/types';

// App
import FieldInput from '@shared/design/components/Field/FieldInput';
import IconButton from '@shared/design/components/IconButton';
import Line from '@shared/design/components/Line';
import Text from '@shared/design/components/Text';
import TextTooltip from '@shared/design/components/TextTooltip';
import ErrorToast from '@shared/design/components/Toast/ErrorToast';
import EmailActionButtons from '@shared/modules/Communication/components/Footer/EmailActionButtons';
import InlineEmailRecipientsSection from '@shared/modules/Communication/components/Footer/InlineEmailRecipientsSection';
import MissingCommunicationInfo from '@shared/modules/Communication/components/Footer/MissingCommunicationInfo';
import EmailTemplateBodyKind from '@shared/modules/Email/enums/EmailTemplateBodyKind';
import EmailForm, {
  EmailRecipientFormType,
  EmailRecipientOptionType,
} from '@shared/modules/Email/forms/EmailForm';
import EmailPreviewForm from '@shared/modules/Email/forms/EmailPreviewForm';
import useEmailPreviewMutation from '@shared/modules/Email/hooks/useEmailPreviewMutation';
import useSendEmailForProjectMutation, {
  WrappedEmailFormType,
} from '@shared/modules/Email/hooks/useSendEmailForProjectMutation';

const Container = Styled.View<{footerExpanded?: boolean}>`
  ${({footerExpanded}) => (footerExpanded ? 'flex: 1;' : 'height: 156px;')};
`;

const ContentContainer = Styled.View`
  flex: 1;
`;

const SubjectButtonRow = Styled.View<{isReply?: boolean}>`
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding-right: 12px;
  background-color: ${({isReply}) => (isReply ? colors.gray.background : undefined)};
`;

const SubjectButtonsContainer = Styled.View`
  flex-direction: row;
  align-items: center;
`;

const ReplySubjectContainer = Styled.View`
  height: 36px;
  justify-content: center;
  padding-horizontal: 12px;
  flex: 1;
`;

const IconButtonV2 = Styled.ButtonV2<{backgroundColor: string}>`
  height: 24px;
  width: 24px;
  border-radius: 14px;
  background-color: ${({backgroundColor}) => backgroundColor || colors.blue.interactive};
  align-items: center;
  justify-content: center;
`;

const RecipientsContainer = Styled.View`
`;

const EmailInputContainer = Styled.View`
  flex: 1;
`;

const Loading = Styled.Loading`
`;

const BottomButtonsContainer = Styled.View`
  flex-direction: row;
  padding-horizontal: 16px;
  padding-bottom: 12px;
  align-items: flex-end;
  justify-content: flex-end;
`;

const getEmailForm = ({
  viewer,
  project,
  replyEmail,
}: {
  viewer: UserModel;
  project: ProjectModel;
  replyEmail?: EmailModel;
}) => {
  if (replyEmail) {
    return EmailForm.newForReply({
      viewer,
      project,
      email: replyEmail,
    });
  }
  return EmailForm.newFromProject({
    viewer,
    project,
  });
};

type InitialEmailFormParams = {
  customer: UserModel;
  viewer: UserModel;
  project: ProjectModel;
  replyEmail?: EmailModel;
};

const PROJECT_CUSTOMER_EMAIL_VALUE = 'PROJECT_CUSTOMER';

const getInitialEmailForm = ({customer, viewer, project, replyEmail}: InitialEmailFormParams) => {
  const form = EmailForm.toForm(getEmailForm({viewer, project, replyEmail}));
  if (!form.toEmailRecipientForms?.length) {
    form.toEmailRecipientForms = [{email: customer.email}];
    form.toEmailRecipientValues = [PROJECT_CUSTOMER_EMAIL_VALUE];
  }
  return EmailForm.toForm(form);
};

// Small wrapper around the SanitizedHTML component, handles scrolling and resetting the inner height when template switches
const HTMLBody = ({
  body,
  emailTemplateId,
  style,
}: {
  body: string;
  emailTemplateId?: string;
  style?: ViewStyleProp;
}) => {
  const emailTemplateIdRef = useRef(emailTemplateId);
  const [renderComponent, setRenderComponent] = useState(true);

  // When the email template changes, briefly hide the component so SanitizedHTML can reset the height
  useEffect(() => {
    if (emailTemplateIdRef.current !== emailTemplateId) {
      setRenderComponent(false);
      setTimeout(() => {
        setRenderComponent(true);
        emailTemplateIdRef.current = emailTemplateId;
      }, 100);
    }
  }, [emailTemplateIdRef.current, emailTemplateId]);

  if (!renderComponent) {
    return null;
  }

  return (
    <ScrollView style={style}>
      <SanitizedHTML rawHTML={body} />
    </ScrollView>
  );
};

const getFormattedRecipientInfo = ({
  emailRecipientForms,
  recipientOptions,
  label,
}: {
  emailRecipientForms: EmailRecipientFormType[];
  recipientOptions: EmailRecipientOptionType[];
  label: string;
}) => {
  let displayEmails = emailRecipientForms.map(
    ({email}) =>
      recipientOptions.find(({email: recipientEmail}) => recipientEmail === email)?.label || email,
  );
  if (!displayEmails.length) {
    return '';
  }
  if (displayEmails.length > 4) {
    displayEmails = [...displayEmails.slice(0, 3), `+${displayEmails.length - 3} more`];
  }
  return `\n${label}: ${displayEmails.join(', ')}`;
};

const getEmailInfoTooltip = (emailForm: Form<WrappedEmailFormType>, user: UserModel) => {
  const {
    toEmailRecipientForms,
    ccEmailRecipientForms,
    bccEmailRecipientForms,
    accountGrantOptions,
    accountGrantId,
  } = emailForm.values.emailForm;
  const selectedAccountGrant = accountGrantOptions?.find(({value}) => value === accountGrantId);

  const defaultEmailLabel = user.supermoveEmailAddressDisplayName;
  const fromEmailInfo = selectedAccountGrant ? selectedAccountGrant.label : defaultEmailLabel;
  const recipientOptions = emailForm.values.emailForm.emailRecipientOptions;

  const toLabel = getFormattedRecipientInfo({
    emailRecipientForms: toEmailRecipientForms,
    recipientOptions,
    label: 'To',
  });
  const ccLabel = getFormattedRecipientInfo({
    emailRecipientForms: ccEmailRecipientForms,
    recipientOptions,
    label: 'Cc',
  });
  const bccLabel = getFormattedRecipientInfo({
    emailRecipientForms: bccEmailRecipientForms,
    recipientOptions,
    label: 'Bcc',
  });

  return `From: ${fromEmailInfo}${toLabel}${ccLabel}${bccLabel}`;
};

const sendTooltip = 'Send';

const submitButtonTooltip = (form: Form<WrappedEmailFormType>, isLoading: boolean) => {
  if (_.get(form.values, 'emailForm.toEmailRecipientForms.length') === 0) {
    return {tooltip: 'Please add at least one recipient', isSubmittable: false};
  }
  if (_.get(form.values, 'emailForm.subject') === '') {
    return {tooltip: 'Please enter a subject', isSubmittable: false};
  }
  if (_.get(form.values, 'emailForm.body') === '') {
    return {tooltip: 'Please enter a body', isSubmittable: false};
  }
  if (isLoading) {
    return {tooltip: '', isSubmittable: false};
  }
  return {tooltip: sendTooltip, isSubmittable: true};
};

const SendEmailContent = ({
  project,
  viewer,
  refetch,
  thread,
  customer,
  setReplyThreadUuid,
  replyThreadUuid,
  footerExpanded,
  setFooterExpanded,
  setHasDraft,
}: {
  project: ProjectModel;
  viewer: UserModel;
  refetch: () => void;
  thread?: ThreadModel;
  customer: UserModel;
  setReplyThreadUuid: (threadUuid?: string) => void;
  replyThreadUuid?: string;
  footerExpanded: boolean;
  setFooterExpanded: (footerExpanded: boolean) => void;
  setHasDraft: (hasDraft: boolean) => void;
}) => {
  // Input editor needs an exact height, so create a container and use that height
  const [inputContainerHeight, setInputContainerHeight] = useState(150);
  const responsive = useResponsive();
  const emails = thread?.allEmails;
  // Rich text editor doesn't reset properly on setting to undefined, briefly hide the editor to force a re-render
  const [hideEmailEditor, setHideEmailEditor] = useState(false);
  // Only auto expand the first time, in case deliberately collapses the field
  // because they want to read other emails
  const firstMobileFocus = useRef(true);

  const handleResetEmail = () => {
    form.setValues({
      emailForm: getInitialEmailForm({
        customer,
        viewer,
        project,
      }),
    });
    form.setErrors({});
    setHideEmailEditor(true);
    setTimeout(() => {
      setHideEmailEditor(false);
    }, 100);
    setReplyThreadUuid(undefined);
  };

  const errorToast = useToast({
    ToastComponent: ErrorToast,
    message: 'Error sending email',
    duration: Infinity,
  });
  const latestEmail = emails?.[emails.length - 1];
  const {form, submitting, handleSubmit} = useSendEmailForProjectMutation({
    project,
    emailForm: getInitialEmailForm({
      customer,
      viewer,
      project,
      replyEmail: latestEmail,
    }),
    onSuccess: () => {
      handleResetEmail();
      setFooterExpanded(false);
      refetch();
    },
    onError: (errors) => {
      console.log({errors});
      const firstError = errors?.find?.((error) => error?.message);
      if (firstError) {
        errorToast.handleToast({message: firstError.message});
      } else {
        errorToast.handleToast();
      }
    },
  });
  const field = 'emailForm';
  const formReplyThreadId = form.values.emailForm.threadId;
  const isReply = !!formReplyThreadId;

  // If the customer email changes, update the form
  useEffect(() => {
    const formCustomerEmailIndex = _.get(form.values, 'emailForm.toEmailRecipientValues')?.indexOf(
      PROJECT_CUSTOMER_EMAIL_VALUE,
    );

    const customerEmailUpdated =
      formCustomerEmailIndex !== -1 &&
      customer.email !==
        _.get(form.values, `emailForm.toEmailRecipientForms.${formCustomerEmailIndex}.email`);

    const customerEmailOption = _.get(form.values, 'emailForm.emailRecipientOptions')?.find(
      ({value}: EmailRecipientOptionType) => value === PROJECT_CUSTOMER_EMAIL_VALUE,
    );
    const customerProjectEmailRecipient = project.projectEmailRecipients?.find(
      ({role}) => role === PROJECT_CUSTOMER_EMAIL_VALUE,
    );
    const projectRecipientsUpdated =
      customerProjectEmailRecipient?.email !== customerEmailOption?.email;

    if (customerEmailUpdated || projectRecipientsUpdated) {
      const newEmailForm = getInitialEmailForm({
        customer,
        viewer,
        project,
        replyEmail: latestEmail,
      });
      const updatedEmailForm = form.values.emailForm;
      updatedEmailForm.toEmailRecipientForms = newEmailForm.toEmailRecipientForms;
      updatedEmailForm.toEmailRecipientValues = newEmailForm.toEmailRecipientValues;
      updatedEmailForm.ccEmailRecipientForms = newEmailForm.ccEmailRecipientForms;
      updatedEmailForm.ccEmailRecipientValues = newEmailForm.ccEmailRecipientValues;
      updatedEmailForm.bccEmailRecipientForms = newEmailForm.bccEmailRecipientForms;
      updatedEmailForm.bccEmailRecipientValues = newEmailForm.bccEmailRecipientValues;
      updatedEmailForm.emailRecipientOptions = newEmailForm.emailRecipientOptions;
      form.setValues({
        emailForm: updatedEmailForm,
        projectId: project.id,
      });
    }
  }, [customer.email, project.projectEmailRecipients]);

  useEffect(() => {
    if (latestEmail?.thread?.id?.toString() !== formReplyThreadId?.toString()) {
      const newEmailForm = getInitialEmailForm({
        customer,
        viewer,
        project,
        replyEmail: latestEmail,
      });
      const updatedEmailForm = form.values.emailForm;
      updatedEmailForm.toEmailRecipientForms = newEmailForm.toEmailRecipientForms;
      updatedEmailForm.threadId = newEmailForm.threadId;
      updatedEmailForm.subject = newEmailForm.subject;
      form.setValues({
        emailForm: updatedEmailForm,
        projectId: project.id,
      });
    }
  }, [latestEmail?.thread?.id, formReplyThreadId]);

  const {
    form: emailPreviewForm,
    handleSubmit: handleEmailPreviewSubmit,
    submitting: isGeneratingPreview,
  } = useEmailPreviewMutation({
    emailPreviewForm: EmailPreviewForm.new({projectId: project.id as unknown as number}),
    onSuccess: ({emailPreview}: {emailPreview: {subject: string; body: string}}) => {
      const emailTemplate = _.get(emailPreviewForm.values, 'emailPreviewForm.emailTemplate');
      const emailForm = EmailForm.toForm(
        isReply
          ? EmailForm.alterFormWithTemplate({
              project,
              form: form.values.emailForm,
              emailTemplate,
              body: emailPreview.body,
              viewer,
            })
          : EmailForm.newFromProjectWithEmailTemplate({
              viewer,
              project,
              emailTemplate: {
                ...emailTemplate,
                subject: emailPreview.subject,
                body: emailPreview.body,
                // Keep the recipients as replying to customer
                toEmailTemplateRecipients: [{role: 'PROJECT_CUSTOMER'}],
                ccEmailTemplateRecipients: [],
                bccEmailTemplateRecipients: [],
              },
            }),
      );
      form.setFieldValue(`${field}`, emailForm);
    },
    onError: (errors: MutationError[]) => console.log({errors}),
  });

  const isLoading = submitting || isGeneratingPreview;
  const subject = _.get(form.values, `${field}.subject`);

  const iconTooltip = (() => {
    if (!responsive.desktop) {
      return undefined;
    }
    return footerExpanded ? 'Collapse' : 'Expand';
  })();

  const {tooltip: submitTooltip, isSubmittable} = submitButtonTooltip(form, isLoading);

  return (
    <ContentContainer>
      <SubjectButtonRow isReply={isReply}>
        {isReply ? (
          <ReplySubjectContainer>
            <Text.Body numberOfLines={1}>
              <Text.Label>Re:</Text.Label> {subject}
            </Text.Body>
          </ReplySubjectContainer>
        ) : (
          <FieldInput.Memoized
            {...form}
            value={subject}
            index={0}
            showErrorMessage={false}
            name={'emailForm.subject'}
            style={{flex: 1, marginLeft: 8}}
            input={{
              placeholder: 'Subject',
              style: {borderWidth: 0, marginLeft: -8},
            }}
          />
        )}
        <SubjectButtonsContainer>
          {footerExpanded ? null : (
            <React.Fragment>
              <IconButton
                tooltip={getEmailInfoTooltip(form, viewer)}
                tooltipPlacement={'top'}
                source={Icon.InfoCircle}
                color={colors.gray.secondary}
                isDisabled
              />
              <Space width={8} />
            </React.Fragment>
          )}
          <IconButton
            tooltip={iconTooltip}
            tooltipPlacement={'top'}
            source={
              footerExpanded
                ? Icon.ArrowDownLeftAndArrowUpRightToCenter
                : Icon.ArrowUpRightAndArrowDownLeftFromCenter
            }
            onPress={() => setFooterExpanded(!footerExpanded)}
          />
        </SubjectButtonsContainer>
      </SubjectButtonRow>
      <Line />
      {footerExpanded ? (
        <RecipientsContainer>
          <InlineEmailRecipientsSection form={form} field={field} user={viewer} />
        </RecipientsContainer>
      ) : null}
      <EmailInputContainer
        onLayout={(event) => setInputContainerHeight(event.nativeEvent.layout.height)}
      >
        {_.get(form.values, `${field}.bodyKind`) === EmailTemplateBodyKind.HTML ? (
          <HTMLBody
            body={_.get(form.values, `${field}.body`)}
            emailTemplateId={_.get(form.values, `${field}.emailTemplateId`)}
            style={{height: inputContainerHeight}}
          />
        ) : (
          <FieldInput
            {...form}
            index={1}
            name={'emailForm.body'}
            component={hideEmailEditor ? () => null : RichTextInput}
            isResponsive
            handleCommandEnter={handleSubmit}
            showErrorMessage={false}
            style={{marginLeft: 8}}
            input={{
              placeholder: 'Write your email here',
              hasAdvancedFormatting: true,
              onFocus: () => {
                // Since there's not a lot of space on tablet/mobile,
                // if the user clicks into the email text, auto-expand the footer
                if (!responsive.desktop && firstMobileFocus.current) {
                  firstMobileFocus.current = false;
                  setFooterExpanded(true);
                }
              },
              setFieldValue: (field: string, value: string) => {
                // If there user types something then deletes it all, editor shows empty p but we want to have the validation from empty body
                if (value === '<p></p>') {
                  form.setFieldValue(field, '');
                  setHasDraft(false);
                } else {
                  form.setFieldValue(field, value);
                  setHasDraft(true);
                }
              },
              hideToolbar: !footerExpanded,
              autoFocus: false,
              style: {borderWidth: 0},
              editorStyle: {
                minHeight: undefined,
                height: inputContainerHeight - (footerExpanded ? 100 : 0),
                marginLeft: -12,
              },
            }}
          />
        )}
      </EmailInputContainer>
      <Space height={4} />
      <BottomButtonsContainer>
        <EmailActionButtons
          form={form}
          project={project}
          field={'emailForm'}
          responsive={responsive}
          refetch={refetch}
          submitting={isLoading}
          selectedEmailTemplateId={_.get(form.values, `${field}.emailTemplateId`)}
          replyThreadUuid={replyThreadUuid}
          handleSelectEmailTemplate={(emailTemplate) => {
            form.setFieldValue(`${field}.emailTemplateId`, emailTemplate.id);
            emailPreviewForm.setFieldValue('emailPreviewForm.emailTemplate', emailTemplate);
            setTimeout(handleEmailPreviewSubmit, 0);
          }}
          handleDiscardEmail={handleResetEmail}
        />
        <Space style={{flex: 1}} />
        <TextTooltip text={submitTooltip} placement={'top'}>
          <IconButtonV2
            disabled={isLoading || !isSubmittable}
            backgroundColor={
              isSubmittable || isLoading ? colors.blue.interactive : colors.gray.secondary
            }
            onPress={handleSubmit}
          >
            {isLoading ? (
              <Loading size={14} color={colors.white} />
            ) : (
              <Icon source={Icon.ArrowUp} size={14} color={colors.white} />
            )}
          </IconButtonV2>
        </TextTooltip>
      </BottomButtonsContainer>
    </ContentContainer>
  );
};

const FooterSendEmail = ({
  customer,
  refetch,
  project,
  replyThreadUuid,
  setReplyThreadUuid,
  footerExpanded,
  setFooterExpanded,
  setHasDraft,
}: {
  customer: UserModel;
  refetch: () => void;
  project: ProjectModel;
  replyThreadUuid?: string;
  setReplyThreadUuid: (replyThreadUuid?: string) => void;
  footerExpanded: boolean;
  setFooterExpanded: (footerExpanded: boolean) => void;
  setHasDraft: (hasDraft: boolean) => void;
}) => {
  const noEmail = !customer.email;
  const {
    data,
    loading,
    refetch: refetchEmailInfo,
  } = useQuery<{
    project: ProjectModel;
    viewer: UserModel;
    threadByUuid?: ThreadModel;
  }>(FooterSendEmail.query, {
    variables: {
      projectUuid: project.uuid,
      threadUuid: replyThreadUuid || '',
    },
  });

  if (noEmail) {
    return (
      <MissingCommunicationInfo
        linkText={'Add an email address'}
        text={`to send new emails to ${customer.fullName}`}
        focusField='email'
        onSuccess={refetch}
        project={project}
        user={customer}
      />
    );
  }

  return (
    <Container footerExpanded={footerExpanded}>
      {/* Show loading indicator if no data for more seemless loading
      The above container also has a loading indicator, and there's a bit of a stutter on first render without this */}
      <DataLoader data={data} loading={loading || !data}>
        {({loadedData}) => {
          return (
            <SendEmailContent
              project={loadedData.project}
              viewer={loadedData.viewer}
              refetch={() => {
                refetchEmailInfo();
                refetch();
              }}
              thread={loadedData.threadByUuid}
              customer={customer}
              setReplyThreadUuid={setReplyThreadUuid}
              replyThreadUuid={replyThreadUuid}
              footerExpanded={footerExpanded}
              setFooterExpanded={setFooterExpanded}
              setHasDraft={setHasDraft}
            />
          );
        }}
      </DataLoader>
    </Container>
  );
};

// --------------------------------------------------
// Data
// --------------------------------------------------

FooterSendEmail.query = gql`
  ${EmailForm.newFromProject.fragment}
  ${EmailForm.newForReply.fragment}
  ${EmailActionButtons.fragment}
  ${InlineEmailRecipientsSection.fragment}

  query FooterSendEmail($projectUuid: String!, $threadUuid: String!) {
    project(uuid: $projectUuid) {
      id
      ...EmailForm_newFromProject
      ...EmailActionButtons_Project
      ...EmailForm_newForReply_Project
    }
    viewer {
      id
      supermoveEmailAddressDisplayName
      ...EmailForm_newFromProject_User
      ...EmailForm_newForReply_User
      ...InlineEmailRecipientsSection_User
    }
    threadByUuid(uuid: $threadUuid) {
      id
      allEmails {
        id
        ...EmailForm_newForReply_Email
        thread {
          id
        }
      }
    }
  }
`;

FooterSendEmail.fragment = gql`
  ${MissingCommunicationInfo.fragment}

  fragment FooterSendEmail_User on User {
    id
    email
    fullName
    organizationId
    ...MissingCommunicationInfo_User
  }

  fragment FooterSendEmail_Project on Project {
    id
    uuid
    ...MissingCommunicationInfo
  }
`;

export default FooterSendEmail;
